import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  OutlinedInput,
  Alert,
  CircularProgress,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormHelperText,
} from "@mui/material";
import { Close, Save, ExpandMore } from "@mui/icons-material";
import { createUser, updateCredits, listAwsSesAccounts } from "../../api/superAdminApi";
import { ASSIGNABLE_ROLES, rolesNeedWbConfig } from "./adminUtils";
import { formatRoleLabel } from "../UserManagement/roleLabels";
import { WB_SX } from "../../ui/ux/uiTokens";

const RegisterUserModal = ({ open, onClose, onUserAdded, defaultRoles = [] }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sesAccounts, setSesAccounts] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    user_role: [],
    pwc: "",
    dwpc: "",
    adwpc: "",
    bec: "",
    dbec: "",
    adbec: "",
    bwc: "",
    dbwc: "",
    adbwc: "",
    waba_id: "",
    phone_number_id: "",
    status: "INACTIVE",
    aws_ses_account_id: "",
  });

  const resetForm = () => ({
    name: "",
    email: "",
    password: "",
    user_role: [],
    pwc: "",
    dwpc: "",
    adwpc: "",
    bec: "",
    dbec: "",
    adbec: "",
    bwc: "",
    dbwc: "",
    adbwc: "",
    waba_id: "",
    phone_number_id: "",
    status: "INACTIVE",
    aws_ses_account_id: "",
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRoleChange = (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      user_role: typeof value === "string" ? value.split(",") : value,
    }));
  };

  useEffect(() => {
    if (open && defaultRoles?.length > 0) {
      setFormData((prev) => ({ ...prev, user_role: [...defaultRoles] }));
    }
    if (open) {
      listAwsSesAccounts({ status: "ACTIVE", per_page: 100 })
        .then(({ accounts }) => setSesAccounts(accounts || []))
        .catch(() => setSesAccounts([]));
    }
  }, [open, defaultRoles]);

  const hasPersonal = formData.user_role.includes("PERSONAL");
  const hasEmail = formData.user_role.includes("EMAIL");
  const hasEmailAdmin = formData.user_role.includes("EMAIL-ADMIN");
  const hasWbRole = rolesNeedWbConfig(formData.user_role);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    if (!formData.name.trim() || !formData.email.trim() || !formData.password) {
      setError("Name, email, and password are required.");
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters.");
      setLoading(false);
      return;
    }

    if (formData.user_role.length === 0) {
      setError("Select at least one role for the new user.");
      setLoading(false);
      return;
    }

    const roleString = formData.user_role.join(",");

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        password: formData.password,
        user_role: roleString,
      };

      if (formData.aws_ses_account_id) {
        payload.aws_ses_account_id = Number(formData.aws_ses_account_id);
      }

      if (hasWbRole && (formData.waba_id || formData.phone_number_id || formData.bwc)) {
        payload.whatsapp_business_config = {
          status: formData.status || "INACTIVE",
          ...(formData.waba_id && { waba_id: formData.waba_id }),
          ...(formData.phone_number_id && { phone_number_id: formData.phone_number_id }),
          ...(formData.bwc && { bwc: parseInt(formData.bwc, 10) }),
          ...(formData.dbwc && { dbwc: parseInt(formData.dbwc, 10) }),
          ...(formData.adbwc && { adbwc: parseInt(formData.adbwc, 10) }),
        };
      }

      const result = await createUser(payload);
      const userId = result?.data?.id;

      if (userId) {
        if (hasPersonal && (formData.pwc || formData.dwpc || formData.adwpc)) {
          const creditPayload = { service: "personal_whatsapp" };
          if (formData.pwc) creditPayload.pwc = parseInt(formData.pwc, 10);
          if (formData.dwpc) creditPayload.dwpc = parseInt(formData.dwpc, 10);
          if (formData.adwpc) creditPayload.adwpc = parseInt(formData.adwpc, 10);
          await updateCredits(userId, creditPayload);
        }
        if (hasEmail && (formData.bec || formData.dbec || formData.adbec)) {
          const creditPayload = { service: "email" };
          if (formData.bec) creditPayload.bec = parseInt(formData.bec, 10);
          if (formData.dbec) creditPayload.dbec = parseInt(formData.dbec, 10);
          if (formData.adbec) creditPayload.adbec = parseInt(formData.adbec, 10);
          await updateCredits(userId, creditPayload);
        }
      }

      setSuccess("User created successfully.");
      setFormData(resetForm());
      if (onUserAdded) onUserAdded();
      setTimeout(() => handleClose(), 1500);
    } catch (err) {
      setError(err.message || "An error occurred while creating the user");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(resetForm());
    setError("");
    setSuccess("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: "92vh" } }}>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Create new user</Typography>
            <Typography variant="body2" color="text.secondary">
              Uses Super-Admin API. Assign roles and optional credits/config.
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small"><Close /></IconButton>
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          {success ? <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert> : null}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Full name" name="name" value={formData.name} onChange={handleChange} required size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" name="email" type="email" value={formData.email} onChange={handleChange} required size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Password" name="password" type="password" value={formData.password} onChange={handleChange} required size="small" helperText="Minimum 6 characters" />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Roles</InputLabel>
                <Select multiple name="user_role" value={formData.user_role} onChange={handleRoleChange} input={<OutlinedInput label="Roles" />} renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => <Chip key={value} label={formatRoleLabel(value)} size="small" />)}
                  </Box>
                )}>
                  {ASSIGNABLE_ROLES.map(({ value, group }) => (
                    <MenuItem key={value} value={value}>
                      <Typography variant="body2">{formatRoleLabel(value)}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{group}</Typography>
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Select one or more roles</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>

          {(hasEmailAdmin || hasEmail) && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>AWS SES account</InputLabel>
                  <Select
                    name="aws_ses_account_id"
                    label="AWS SES account"
                    value={formData.aws_ses_account_id}
                    onChange={handleChange}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {sesAccounts.map((account) => (
                      <MenuItem key={account.id} value={String(account.id)}>
                        {account.name} ({account.aws_region})
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {hasEmailAdmin
                      ? "Required before EMAIL-ADMIN users can create domains."
                      : "Optional; assign if this user will manage domains."}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          )}

          {(hasPersonal || hasEmail) && (
            <Accordion defaultExpanded disableGutters elevation={0} sx={{ mt: 2, border: "1px solid", borderColor: "divider" }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>Personal & email credits (optional)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {hasPersonal && ["pwc", "dwpc", "adwpc"].map((field) => (
                    <Grid item xs={12} sm={4} key={field}>
                      <TextField fullWidth label={field.toUpperCase()} name={field} type="number" value={formData[field]} onChange={handleChange} size="small" inputProps={{ min: 0 }} />
                    </Grid>
                  ))}
                  {hasEmail && ["bec", "dbec", "adbec"].map((field) => (
                    <Grid item xs={12} sm={4} key={field}>
                      <TextField fullWidth label={field.toUpperCase()} name={field} type="number" value={formData[field]} onChange={handleChange} size="small" inputProps={{ min: 0 }} />
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}

          {hasWbRole && (
            <Accordion disableGutters elevation={0} sx={{ mt: 1, border: "1px solid", borderColor: "divider" }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>WhatsApp Business config (optional)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="WABA ID" name="waba_id" value={formData.waba_id} onChange={handleChange} size="small" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Phone number ID" name="phone_number_id" value={formData.phone_number_id} onChange={handleChange} size="small" />
                  </Grid>
                  {["bwc", "dbwc", "adbwc"].map((field) => (
                    <Grid item xs={12} sm={4} key={field}>
                      <TextField fullWidth label={field.toUpperCase()} name={field} type="number" value={formData[field]} onChange={handleChange} size="small" inputProps={{ min: 0 }} />
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={6}>
                    <TextField select fullWidth label="WB config status" name="status" value={formData.status} onChange={handleChange} size="small">
                      <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                      <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button onClick={handleClose} disabled={loading} sx={WB_SX.secondaryButton}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={loading} startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Save />} sx={WB_SX.primaryButton}>
            {loading ? "Creating…" : "Create user"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RegisterUserModal;
